import React, { useCallback, useEffect, useState, useMemo } from "react";
import { Box, Button, Grid, Typography } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import { useHistory } from "react-router-dom";
import Table from "../shared/Table";
import { useModals } from "../../hooks/useModals";
import { useSnackbar } from "notistack";
import EditIcon from "@material-ui/icons/Edit";
import { useAppContext } from "../../api/AppContext";
import {
  getLanguageChipStyles,
  getLocalizedField,
  getPreferredLanguageFromUser,
  getValidLanguagesFromUser,
  isTagNameCompleteForSave,
  LANGUAGE_CHIP_LABELS,
} from "../shared/languageConfig";

const TagsList = () => {
  const [localTags, setLocalTags] = useState([]);
  const modals = useModals();
  const { newsTags, fetchNewsTags, deleteNewsTag, isNewsTagsLoading, user } =
    useAppContext();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const validLanguages = useMemo(() => getValidLanguagesFromUser(user), [user]);
  const [activeTableLanguage, setActiveTableLanguage] = useState(() =>
    getPreferredLanguageFromUser(user, validLanguages)
  );

  useEffect(() => {
    const preferred = getPreferredLanguageFromUser(user, validLanguages);
    setActiveTableLanguage((current) =>
      validLanguages.includes(current) ? current : preferred
    );
  }, [user, validLanguages]);

  const fetchTags = useCallback(async () => {
    await fetchNewsTags();
  }, [fetchNewsTags]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  const handleCreate = useCallback(() => {
    history.push(`/news/tags/new`, {
      isNew: true,
    });
  }, [history]);

  const handleEdit = useCallback(
    (tag) => {
      history.push(`/news/tags/${tag.id}`, { tag });
    },
    [history]
  );

  const handleDelete = useCallback(
    (tag) => {
      const displayName =
        getLocalizedField(tag.tagname, activeTableLanguage) ||
        getLocalizedField(tag.tagname, "en") ||
        "this tag";

      modals.openConfirmation({
        onConfirm: async () => {
          try {
            await deleteNewsTag({ id: tag.id });
            fetchTags();
            enqueueSnackbar("Tag deleted successfully!", {
              variant: "success",
            });
          } catch (error) {
            enqueueSnackbar("Error deleting tag", { variant: "error" });
          }
        },
        text: `You want to delete tag "${displayName}"`,
      });
    },
    [activeTableLanguage, deleteNewsTag, enqueueSnackbar, fetchTags, modals]
  );

  const columns = useMemo(
    () => [
      {
        title: "Name",
        field: "tagname",
        width: "50%",
        customSort: (a, b) => {
          const aName = getLocalizedField(
            a.tagname,
            activeTableLanguage
          ).toLowerCase();
          const bName = getLocalizedField(
            b.tagname,
            activeTableLanguage
          ).toLowerCase();
          return aName.localeCompare(bName);
        },
        customFilterAndSearch: (term, rowData) => {
          const name = getLocalizedField(rowData.tagname, activeTableLanguage);
          return name.toLowerCase().includes(term.toLowerCase());
        },
        render: (rowData) => (
          <Box>
            <Typography variant="body1">
              {getLocalizedField(rowData.tagname, activeTableLanguage) ||
                "No name"}
            </Typography>
          </Box>
        ),
      },
      {
        title: "Color",
        field: "color",
        width: "50%",
        customSort: (a, b) => {
          const aColor = (a.color || "").toLowerCase();
          const bColor = (b.color || "").toLowerCase();
          return aColor.localeCompare(bColor);
        },
        customFilterAndSearch: (term, rowData) => {
          const color = rowData.color || "";
          return color.toLowerCase().includes(term.toLowerCase());
        },
        render: (rowData) => (
          <Box alignItems="center" display="flex">
            <Box
              style={{
                backgroundColor: rowData.color,
                borderRadius: 4,
                height: 20,
                marginRight: 8,
                width: 20,
              }}
            />
            <Typography variant="body2">
              {rowData.color || "No color"}
            </Typography>
          </Box>
        ),
      },
    ],
    [activeTableLanguage]
  );

  const actions = useMemo(
    () => [
      {
        icon: (props) => <EditIcon {...props} style={{ color: "#0000008a" }} />,
        tooltip: "Edit",
        onClick: (event, rowData) => handleEdit(rowData),
      },
      {
        icon: (props) => (
          <DeleteIcon {...props} style={{ color: "#0000008a" }} />
        ),
        tooltip: "Delete",
        onClick: (event, rowData) => handleDelete(rowData),
      },
    ],
    [handleEdit, handleDelete]
  );

  const tableActions = useMemo(
    () => [
      <Button
        key="add"
        color="primary"
        endIcon={<AddIcon />}
        variant="contained"
        onClick={handleCreate}
      >
        Add tag
      </Button>,
    ],
    [handleCreate]
  );

  const options = useMemo(
    () => ({
      pageSize: 10,
      pageSizeOptions: [5, 10, 15],
      search: true,
      filtering: false,
    }),
    []
  );

  const languageChips = useMemo(
    () => (
      <Box display="flex" flexWrap="wrap" pt={0.5}>
        {validLanguages.map((language) => (
          <Button
            key={language}
            size="small"
            style={{
              ...getLanguageChipStyles(activeTableLanguage === language),
              borderRadius: 20,
              boxShadow: "none",
              marginBottom: 8,
              marginRight: 8,
              minWidth: 90,
            }}
            variant="contained"
            onClick={() => setActiveTableLanguage(language)}
          >
            {LANGUAGE_CHIP_LABELS[language] || language.toUpperCase()}
          </Button>
        ))}
      </Box>
    ),
    [activeTableLanguage, validLanguages]
  );

  useEffect(() => {
    setLocalTags(
      (newsTags || []).map((t) => ({
        color: t.tagcolor || "#000000",
        id: t.id,
        tagname: t.tagname || {},
        tagcolor: t.tagcolor || "#000000",
        cids: Array.isArray(t.cids) ? t.cids : [],
      }))
    );
  }, [newsTags]);

  const filteredTags = useMemo(
    () =>
      localTags.filter((tag) =>
        isTagNameCompleteForSave(tag.tagname, activeTableLanguage)
      ),
    [activeTableLanguage, localTags]
  );

  return (
    <Box
      boxSizing="border-box"
      display="flex"
      flexDirection="column"
      height="100%"
      p={4}
      width="100%"
    >
      <Grid container direction="column" spacing={4}>
        <Grid item>
          <Table
            actions={actions}
            columns={columns}
            data={filteredTags}
            isLoading={isNewsTagsLoading}
            languageChips={languageChips}
            options={options}
            refetch={fetchTags}
            tableActions={tableActions}
            title="Tags"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default TagsList;
